<script lang="ts">
	import type { MaterialProperty } from './types/materialProperty'
	import { DefaultMaterialProperty } from './types/materialProperty'
	import { t } from '$i18n/i18n'
	import ActionMenu from '#lib/organisms/ActionMenu.svelte'
	import ActionMenuItem from '#lib/molecules/ActionMenuItem.svelte'
	import { modals } from 'svelte-modals'
	import MaterialPropertyModal from './MaterialPropertyModal.svelte'
	import Modal from '#lib/organisms/Modal.svelte'
	import { applyFilters } from '$utils/filter'
	import { changeUrl } from '$utils/misc'
	import iconTrashRed from '@/icons/icon_trash_red.svg'
	import { mmpvState } from '#lib/mmpv/mmpv.state.svelte.js'
	import { deleteProperty } from './mmpv-properties.controller.svelte'

	interface Props {
		onUpdateProperties?: (propertyId: string) => void
	}

	let { onUpdateProperties }: Props = $props()

	const filters = $derived(mmpvState.filters)

	function selectProperty(property: MaterialProperty) {
		modals.open(MaterialPropertyModal, {
			materialProperty: {
				...DefaultMaterialProperty(),
				...mmpvState.getMaterialProperty(property.id)
			},
			materialId: undefined,
			isNew: false,
			parent: null
		})
		changeUrl(`materialProperty/${property.id}`, window)
	}

	function handleDelete(property: MaterialProperty) {
		modals.open(Modal, {
			title: $t('UI.mmpv.delete.modal.title', { entity: property.name }),
			message: $t('UI.mmpv.delete.property.modal.message', { property: property.name }),
			denyText: $t('UI.delete'),
			onDeny: () => {
				deleteProperty(property, onUpdateProperties)
				modals.close()
			}
		})
	}
</script>

<div class="properties-container">
	<div class="properties-table">
		<div class="row-wrapper">
			<div>{$t('UI.mmpv.properties.name')}</div>
			<div>{$t('UI.mmpv.properties.type')}</div>
			<div>{$t('UI.mmpv.properties.flags')}</div>
			<div>{$t('UI.mmpv.properties.links')}</div>
			<div>{$t('UI.mmpv.catalogs.action')}</div>
		</div>
		{#each [...mmpvState.materialProperties.values()]
			.sort((a, b) => (a.priority ?? Number.MAX_SAFE_INTEGER) - (b.priority ?? Number.MAX_SAFE_INTEGER))
			.sort((a, b) => (a.type > b.type ? 1 : b.type > a.type ? -1 : 0))
			.filter((prop) => applyFilters(filters, prop)) as property (property.id)}
			<!--sorting by priority and type -->
			{@const flags = property.flags
				.map((f) => $t(`PropertySystemFlag.${f.toString()}`))
				.sort()
				.join(', ')}
			{@const propertyParents = mmpvState.getParentsNamesAsString(property.id)}
			<div
				class="row-wrapper"
				role="button"
				tabindex="0"
				onclick={() => selectProperty(property)}
				onkeydown={(e) => {
					if (e.key !== 'Enter' && e.key !== ' ') return
					if (e.key === ' ') e.preventDefault() // prevent page scroll on Space
					selectProperty(property)
				}}
			>
				<div title={property.name || ''}>{property.name || ''}</div>
				<div>{$t('UI.mmpv.properties.types.' + property.type) || ''}</div>
				<div>{property.flags.length > 0 ? flags : ''}</div>
				<div>{propertyParents ?? ''}</div>
				<div style="position: relative">
					<ActionMenu
						titleAttr={$t('UI.mmpv.actionMenu.titleAttribute')}
						appendToElement={document.body}
					>
						{#snippet content()}
							<ActionMenuItem
								label={$t('UI.delete')}
								iconSrc={iconTrashRed}
								onclick={() => handleDelete(property)}
							/>
						{/snippet}
					</ActionMenu>
				</div>
			</div>
		{/each}
	</div>
</div>

<style>
	@import '$styles/global.scss';

	.properties-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;

		width: 100%;
	}

	.properties-table {
		display: grid;
		grid-template-columns:
			minmax(150px, 0.5fr) minmax(150px, 0.33fr) repeat(2, minmax(150px, 0.5fr))
			minmax(60px, 0.15fr);
		grid-template-rows: 1fr; /* remove for unequal rows */

		font-family: Roboto, sans-serif;
		line-height: 18px;
		font-weight: 400;
		font-size: 0.75rem;
		color: black;

		width: 100%;

		& .row-wrapper {
			display: contents;

			& > div {
				text-align: start;
				border-bottom: 1px solid var(--color-gray-100);
				padding: 0.75rem 0.75rem;
				text-overflow: ellipsis;
				overflow: hidden;
				white-space: nowrap;

				&:last-child {
					display: flex;
					flex-direction: row;
					align-items: center;
					justify-content: center;
				}

				&:last-child {
					overflow: visible;
				}

				&:hover {
					cursor: pointer;
				}
			}

			&:first-child > div {
				border-bottom: 1px solid var(--primary-green);

				font-family: Roboto, sans-serif;
				line-height: 18px;
				font-weight: 400;
				font-size: 0.875rem;
				color: black;
			}
		}
	}
</style>
